import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, FileText, Building2, User, Calendar } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useQuoteSkus } from '../../hooks';
import { QuoteStatusBadge } from '../QuoteStatusBadge';
import type { Quote } from '../../types';

interface QuoteViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote | null;
  onViewPdf: (quoteId: string) => void;
}

export function QuoteViewModal({
  open,
  onOpenChange,
  quote,
  onViewPdf,
}: QuoteViewModalProps) {
  // Fetch SKUs when modal is open
  const { data: skus, isLoading: skusLoading } = useQuoteSkus(
    quote?.id,
    open && !!quote?.id
  );

  if (!quote) return null;

  const total = parseFloat(quote.total) || 0;
  const subtotal = parseFloat(quote.subtotal) || 0;
  const taxAmount = parseFloat(quote.taxAmount) || 0;

  const handleViewPdf = () => {
    onViewPdf(quote.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              Quote #{quote.quoteNumber}
              <QuoteStatusBadge status={quote.status} />
            </DialogTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleViewPdf}>
                <FileText className="h-4 w-4 mr-1" />
                View PDF
              </Button>
              {quote.hubspotLink && (
                <a
                  href={quote.hubspotLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    HubSpot
                  </Button>
                </a>
              )}
              {quote.gdriveLink && (
                <a
                  href={quote.gdriveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Drive
                  </Button>
                </a>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Account & Project Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Account:</span>
                <span className="font-medium">{quote.accountName || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Project:</span>
                <span className="font-medium">{quote.projectName || '—'}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Account Manager:</span>
                <span className="font-medium">{quote.salesManagerName || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Contact:</span>
                <span className="font-medium">{quote.contractualManagerName || '—'}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Created
              </div>
              <p className="font-medium">
                {quote.createdAt ? formatDate(quote.createdAt) : '—'}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Expiration
              </div>
              <p className="font-medium">
                {quote.expirationDate ? formatDate(quote.expirationDate) : '—'}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Accepted
              </div>
              <p className="font-medium">
                {quote.acceptedDate ? formatDate(quote.acceptedDate) : '—'}
              </p>
            </div>
          </div>

          <Separator />

          {/* SKUs / Line Items */}
          <div>
            <h3 className="font-medium mb-3">Line Items</h3>
            {skusLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : !skus || skus.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No line items available
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">SKU</th>
                      <th className="text-left px-3 py-2 font-medium">Description</th>
                      <th className="text-right px-3 py-2 font-medium">Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Unit Price</th>
                      <th className="text-right px-3 py-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skus.map((sku, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{sku.sku}</td>
                        <td className="px-3 py-2">{sku.description || sku.name}</td>
                        <td className="px-3 py-2 text-right">{sku.quantity}</td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(sku.unitPrice, quote.currency || 'CAD')}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(sku.quantity * sku.unitPrice, quote.currency || 'CAD')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Separator />

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>{formatCurrency(subtotal, quote.currency || 'CAD')}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Tax ({quote.taxCategory || 'N/A'}):
                  </span>
                  <span>{formatCurrency(taxAmount, quote.currency || 'CAD')}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Total:</span>
                <span className="text-lg">
                  {formatCurrency(total, quote.currency || 'CAD')}
                </span>
              </div>
              <div className="text-right">
                <Badge variant="secondary">{quote.currency || 'CAD'}</Badge>
              </div>
            </div>
          </div>

          {/* Description */}
          {quote.description && (
            <>
              <Separator />
              <div>
                <h3 className="font-medium mb-2">Description</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {quote.description}
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
